import { useState, useEffect } from 'react';
import PhoneShell from '../components/shared/PhoneShell';
import BackHeader from '../components/shared/BackHeader';
import ProfileSubNav from '../components/shared/ProfileSubNav';
import { useLang } from '../i18n';

const API = process.env.REACT_APP_API_BASE_URL;

/* jj/mm/aaaa (saisie) → aaaa-mm-jj (colonne DATE attendue par le back). */
function toApiDate(s) {
  if (!s) return '';
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return s; // déjà au format ISO ou autre : on laisse tel quel
}

/* aaaa-mm-jj (back) → jj/mm/aaaa (affichage). */
function toDisplayDate(s) {
  if (!s || String(s).startsWith('0000')) return '';
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
}

const css = `
  .ei { padding: 4px 16px 28px; }
  .ei-card { margin-top: 12px; padding: 14px; border-radius: 12px; background: #16142e; box-shadow: inset 0 0 0 1px rgba(255,255,255,.07); }
  .ei-title { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 700; color: #fff; margin-bottom: 14px; }
  .ei-title .lk { width: 15px; height: 15px; }
  .ei-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 11px; }
  .ei-field.full { grid-column: 1 / -1; }
  .ei-flabel { font-size: 11px; color: #9b9ab0; margin-bottom: 6px; }
  .ei-input { height: 44px; background: #100e26; border-radius: 9px; box-shadow: inset 0 0 0 1px rgba(255,255,255,.08); display: flex; align-items: center; gap: 8px; padding: 0 12px; transition: box-shadow .15s; }
  .ei-input:focus-within { box-shadow: inset 0 0 0 1px #76e1ff; }
  .ei-input input { flex: 1; background: none; border: none; outline: none; color: #fff; font-size: 14px; font-family: inherit; width: 100%; }
  .ei-input input::placeholder { color: #46455e; }
  .ei-input input:disabled { color: #8a89a0; }
  .ei-input.dis { background: #12101f; }
  .ei-note { font-size: 10px; color: #6a6982; margin-top: 5px; font-style: italic; }
  .ei-eye { width: 18px; height: 18px; flex: none; cursor: pointer; }
  .ei-save { display: flex; align-items: center; justify-content: center; gap: 8px; height: 48px; border-radius: 11px; margin-top: 16px; width: 100%; background: linear-gradient(90deg, #8187f6, #76e1ff); color: #fff; font-size: 15px; font-weight: 700; border: none; font-family: inherit; cursor: pointer; }
  .ei-save:disabled { opacity: .5; cursor: not-allowed; }
  .ei-toast { margin: 10px 0 0; padding: 10px 14px; border-radius: 9px; font-size: 13px; font-weight: 600; }
  .ei-toast.ok { background: rgba(0,212,170,.12); color: #00d4aa; }
  .ei-toast.err { background: rgba(255,59,59,.12); color: #ff6b6b; }
`;

function Eye({ off }) {
  return (
    <span className="ei-eye">
      <svg viewBox="0 0 20 20" fill="none">
        <path d="M1.5 10S4.5 4.5 10 4.5 18.5 10 18.5 10 15.5 15.5 10 15.5 1.5 10 1.5 10z" stroke="#757490" strokeWidth="1.3" />
        <circle cx="10" cy="10" r="2.5" stroke="#757490" strokeWidth="1.3" />
        {off ? <path d="M3 3l14 14" stroke="#757490" strokeWidth="1.3" strokeLinecap="round" /> : null}
      </svg>
    </span>
  );
}

function EiField({ label, name, value, onChange, placeholder, full, disabled, note, password }) {
  const [show, setShow] = useState(false);
  return (
    <div className={'ei-field' + (full ? ' full' : '')}>
      <div className="ei-flabel">{label}</div>
      <label className={'ei-input' + (disabled ? ' dis' : '')}>
        <input
          name={name}
          value={value || ''}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          type={password && !show ? 'password' : 'text'}
        />
        {password ? <button type="button" onClick={() => setShow(!show)}><Eye off={show} /></button> : null}
      </label>
      {note ? <div className="ei-note">{note}</div> : null}
    </div>
  );
}

export default function ProfileEdit() {
  const { t } = useLang();
  const [user] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
  });

  const [form, setForm] = useState({
    prenom:   user?.prenom   || '',
    nom:      user?.nom      || '',
    username: user?.username || '',
    email:    user?.email    || '',
    telephone: user?.telephone || '',
    birthdate: toDisplayDate(user?.date_naissance) || user?.birthdate || '',
    adresse:  user?.adresse  || '',
    ville:    user?.ville    || '',
    code_postal: user?.code_postal || '',
    pays:     user?.pays     || '',
  });
  const [passwords, setPasswords] = useState({ new_password: '', confirm_password: '' });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  /* Le localStorage (issu de login.php) et infos.php ne renvoient pas
     tél/adresse/ville/CP/pays/date_naissance → on recharge le profil complet via
     profile.php pour pré-remplir le formulaire. Dégradation gracieuse : si l'appel
     échoue (404/erreur), les champs seedés depuis le localStorage restent affichés. */
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    fetch(`${API}/api/user/profile.php?id=${user.id}`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled || !d?.success) return;
        const u = d.data || d.user || d;
        if (!u || typeof u !== 'object') return;
        setForm((prev) => ({
          prenom:   u.prenom   ?? prev.prenom,
          nom:      u.nom      ?? prev.nom,
          username: u.username ?? prev.username,
          email:    u.email    ?? prev.email,
          telephone: u.telephone ?? prev.telephone,
          birthdate: toDisplayDate(u.date_naissance) || prev.birthdate,
          adresse:  u.adresse  ?? prev.adresse,
          ville:    u.ville    ?? prev.ville,
          code_postal: u.code_postal ?? prev.code_postal,
          pays:     u.pays     ?? prev.pays,
        }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.id]);

  const handleChange = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));
  const handlePwChange = (e) => setPasswords(p => ({ ...p, [e.target.name]: e.target.value }));

  const showToast = (type, msg) => { setToast({ type, msg }); setTimeout(() => setToast(null), 3500); };

  const handleSave = async () => {
    if (passwords.new_password && passwords.new_password !== passwords.confirm_password) {
      showToast('err', t('profile.passwordMismatch')); return;
    }
    setLoading(true);
    try {
      // Le back attend `date_naissance` au format aaaa-mm-jj (pas `birthdate`).
      const { birthdate, ...rest } = form;
      const dateNaissance = toApiDate(birthdate);
      const body = { user_id: user?.id, ...rest, date_naissance: dateNaissance };
      // update.php lit le champ `password` (et le hash avec password_hash).
      if (passwords.new_password) body.password = passwords.new_password;
      const res  = await fetch(`${API}/api/user/update.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        const updated = { ...user, ...rest, date_naissance: dateNaissance };
        localStorage.setItem('user', JSON.stringify(updated));
        window.dispatchEvent(new Event('userUpdated'));
        showToast('ok', t('profile.saveSuccess'));
        setPasswords({ new_password: '', confirm_password: '' });
      } else {
        showToast('err', data.message || t('profile.saveError'));
      }
    } catch {
      showToast('err', t('common.serverError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PhoneShell activeTab="">
      <style>{css}</style>
      <BackHeader title={t('profile.title')} />
      <ProfileSubNav />
      <div className="ei">
        {toast && <div className={'ei-toast ' + toast.type}>{toast.msg}</div>}

        <div className="ei-card">
          <div className="ei-title">{t('profile.personalInfo')}</div>
          <div className="ei-grid">
            <EiField label={t('profile.firstName')} name="prenom"   value={form.prenom}   onChange={handleChange} />
            <EiField label={t('profile.lastName')}  name="nom"      value={form.nom}      onChange={handleChange} />
            <EiField label={t('profile.username')}  name="username" value={form.username} onChange={handleChange} full />
            <EiField label={t('profile.email')} name="email" value={form.email} onChange={handleChange} full disabled note={t('profile.emailNote')} />
            <EiField label={t('profile.phone')}     name="telephone"  value={form.telephone}  onChange={handleChange} placeholder={t('profile.phonePlaceholder')} />
            <EiField label={t('profile.birthdate')} name="birthdate" value={form.birthdate} onChange={handleChange} placeholder={t('profile.birthdatePlaceholder')} />
          </div>
        </div>

        <div className="ei-card">
          <div className="ei-title">{t('profile.addressSection')}</div>
          <div className="ei-grid">
            <EiField label={t('profile.address')}    name="adresse"     value={form.adresse}     onChange={handleChange} placeholder={t('profile.addressPlaceholder')} full />
            <EiField label={t('profile.city')}       name="ville"       value={form.ville}       onChange={handleChange} placeholder={t('profile.cityPlaceholder')} />
            <EiField label={t('profile.postalCode')} name="code_postal" value={form.code_postal} onChange={handleChange} placeholder={t('profile.postalCodePlaceholder')} />
            <EiField label={t('profile.country')}    name="pays"        value={form.pays}        onChange={handleChange} placeholder={t('profile.countryPlaceholder')} full />
          </div>
        </div>

        <div className="ei-card">
          <div className="ei-title">
            <svg className="lk" viewBox="0 0 16 16" fill="none">
              <rect x="3" y="7" width="10" height="7" rx="2" stroke="#76e1ff" strokeWidth="1.4" />
              <path d="M5.5 7V5.3a2.5 2.5 0 015 0V7" stroke="#76e1ff" strokeWidth="1.4" />
            </svg>
            {t('profile.changePassword')}
          </div>
          <div className="ei-note" style={{ marginTop: 0, marginBottom: 12 }}>{t('profile.passwordNote')}</div>
          <div className="ei-grid">
            <EiField label={t('profile.newPassword')}     name="new_password"     value={passwords.new_password}     onChange={handlePwChange} placeholder={t('profile.newPassword')}     password full />
            <EiField label={t('profile.confirmPassword')} name="confirm_password" value={passwords.confirm_password} onChange={handlePwChange} placeholder={t('profile.confirmPassword')} password full />
          </div>
        </div>

        <button className="ei-save" onClick={handleSave} disabled={loading}>
          {loading ? t('profile.saving') : t('profile.saveChanges')}
        </button>
      </div>
    </PhoneShell>
  );
}
